import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Calendar,
  DatePicker,
  Input as AntInput,
  Input,
  Popover,
  Select,
  Skeleton,
  Space,
  Table,
} from "antd";
import {
  CaretLeftOutlined,
  CaretRightOutlined,
  CalendarOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  RightOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import "./AbandonedCart.css";
import CustomSelect from "../../../components/SelectableDropdown/CustomSelect/CustomSelect";
import {
  useLazyGetAbandonedCartListQuery,
  useLazyGetDownloadAbandonedCartReportQuery,
  useLazyGetReportingManagersQuery,
  useUpdateAbandonedCartStatusMutation,
} from "../../../apis/abandonedcartAPI";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../NotificationToast/NotificationToast";
import {
  dateFormat,  
} from "../../../commonFuntions/CommonUtilFunctions";
import { baseUrl } from "../../../commonUtils/commonUtils";

const formatMoney = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;
const statusLabelMap = { abandoned: "Abandoned", recovered: "Recovered", lost: "Lost" };
const { RangePicker } = DatePicker;
const downloadOptions = [
  {
    key: 1,
    value: "Excel",
    label: "Excel",
  },
];

const chipClasses = ["chip-blue", "chip-gray", "chip-amber", "chip-teal", "chip-purple"];
const toInitials = (name = "") =>
  String(name)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("") || "NA";

const getWeekLabel = (dateValue) => {
  const d = new Date(dateValue.year(), dateValue.month(), dateValue.date());
  const dayNum = d.getDay() || 7;
  d.setDate(d.getDate() + 4 - dayNum);
  const yearStart = new Date(d.getFullYear(), 0, 1);
  const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return `W${weekNo}`;
};

const getStatusOptionLabel = (text) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M3 6h18M7 12h10M11 18h2"></path>
    </svg>
    <span>{text}</span>
  </span>
);

const AbandonedCartList = ({
  showHeader = true,
  showInfoBanner = true,
  showSummary = true,
  showToolbar = true,
  tableTitle = "",
  rootClassName = "",
}) => {
  const [getAbandonedCartListApi, { data: abandonedCartListData, isFetching: isListFetching }] =
    useLazyGetAbandonedCartListQuery();
  const [getDownloadAbandonedCartReportApi] =
    useLazyGetDownloadAbandonedCartReportQuery();
  const [getReportingManagersApi, { data: reportingManagersData }] =
    useLazyGetReportingManagersQuery();
  const [updateAbandonedCartStatusApi] = useUpdateAbandonedCartStatusMutation();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [viewFilter, setViewFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortKey, setSortKey] = useState("newest_cart");
  const [currentPage, setCurrentPage] = useState(1);
  const [dateLabel, setDateLabel] = useState(dayjs().format("MMMM YYYY"));
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState("this-month");
  const [periodAnchorDate, setPeriodAnchorDate] = useState(dayjs());
  const [calendarValue, setCalendarValue] = useState(dayjs("2026-05-06"));
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [activeRangeField, setActiveRangeField] = useState("start");
  const [assignedById, setAssignedById] = useState({});
  const [assignOpenFor, setAssignOpenFor] = useState(null);
  const [assignSearch, setAssignSearch] = useState("");
  const customRangeWrapRef = useRef(null);

  useEffect(() => {
    getReportingManagersApi();
  }, [getReportingManagersApi]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchText.trim());
    }, 350);
    return () => clearTimeout(t);
  }, [searchText]);

  const assigneeOptions = useMemo(() => {
    const rows =
      reportingManagersData?.data?.results ||
      reportingManagersData?.data ||
      reportingManagersData?.results ||
      [];
    return (Array.isArray(rows) ? rows : []).map((user, idx) => {
      const name =
        user?.reporting_person ||
        user?.full_name ||
        user?.name ||
        user?.username ||
        user?.email ||
        `User ${idx + 1}`;
      return {
        id: user?.id,
        value: name,
        label: name,
        initials: toInitials(name),
        chip: chipClasses[idx % chipClasses.length],
        searchText: String(name).toLowerCase(),
      };
    });
  }, [reportingManagersData]);

  const apiRows = useMemo(() => {
    const rows =
      abandonedCartListData?.data?.results ||
      abandonedCartListData?.data ||
      abandonedCartListData?.results ||
      [];

    return (Array.isArray(rows) ? rows : []).map((row, idx) => {
      const id = row?.cart_number || row?.cart_id || row?.id || `row-${idx}`;
      const rawStatus = (row?.status || row?.cart_status || "abandoned").toLowerCase();
      const normalizedStatus = ["abandoned", "recovered", "lost"].includes(rawStatus)
        ? rawStatus
        : "abandoned";
      const itemsObj =
        row?.items_count && typeof row.items_count === "object" ? row.items_count : null;
      const itemsCount =
        Number(
          itemsObj?.sku_count ??
            row?.items_count ??
            row?.total_items ??
            row?.items ??
            0
        ) || 0;
      const unitsCount =
        Number(itemsObj?.units ?? row?.units_count ?? row?.total_units ?? row?.units ?? 0) || 0;
      return {
        ...row,
        id,
        customer_name:
          row?.customer?.name || row?.customer_name || row?.name || "-",
        customer_mobile:
          row?.customer?.phone_number ||
          row?.customer_mobile ||
          row?.phone_number ||
          row?.mobile ||
          "-",
        location: row?.location || row?.city || "-",
        items_summary:
          itemsObj?.label ||
          row?.items_summary ||
          `${itemsCount} SKUs${unitsCount ? ` · ${unitsCount} units` : ""}`,
        cart_total: Number(row?.cart_total ?? row?.cart_value ?? row?.total ?? 0) || 0,
        abandoned_text: row?.abandoned_text || row?.updated_at_text || "-",
        status: normalizedStatus,
        hot: Number(row?.cart_total ?? row?.cart_value ?? row?.total ?? 0) > 20000,
        assigned_to:
          row?.assigned_to?.reporting_person ||
          row?.assigned_to?.name ||
          row?.assigned_user_name ||
          row?.assigned_to ||
          undefined,
        assigned_to_id: row?.assigned_to?.id || undefined,
      };
    });
  }, [abandonedCartListData]);

  useEffect(() => {
    if (!apiRows.length) return;
    setAssignedById((prev) => {
      const next = { ...prev };
      apiRows.forEach((row) => {
        if (next[row.id] === undefined && row.assigned_to) {
          next[row.id] = row.assigned_to;
        }
      });
      return next;
    });
  }, [apiRows]);

  const presets = [
    { key: "today", label: "Today", sub: "6 May 2026" },
    { key: "this-week", label: "This week", sub: "4 - 10 May 2026 · W19" },
    { key: "this-month", label: "This month", sub: "May 2026", default: true },
    { key: "this-quarter", label: "This quarter", sub: "Apr - Jun 2026 · Q2" },
    { key: "this-year", label: "This year", sub: "Jan - Dec 2026" },
    { divider: true },
    { key: "last-week", label: "Last week", sub: "27 Apr - 3 May · W18" },
    { key: "last-month", label: "Last month", sub: "Apr 2026" },
    { key: "all-time", label: "All time", sub: "" },
    { divider: true },
    { key: "custom", label: "Custom range", sub: "", custom: true },
  ];

  const handlePresetClick = (key) => {
    setCurrentPage(1);
    setSelectedPreset(key);
    setPeriodAnchorDate(dayjs());
    if (key === "today") setDateLabel(`Today · ${dayjs().format("D MMM YYYY")}`);
    if (key === "this-week") {
      const weekStart = dayjs().startOf("week");
      setDateLabel(`Week of ${weekStart.format("D MMM YYYY")} · ${getWeekLabel(weekStart)}`);
    }
    if (key === "this-month") setDateLabel(dayjs().format("MMMM YYYY"));
    if (key === "this-quarter") setDateLabel("This quarter");
    if (key === "this-year") setDateLabel(dayjs().format("YYYY"));
    if (key === "last-week") {
      const lastWeek = dayjs().subtract(1, "week");
      setPeriodAnchorDate(lastWeek);
      const weekStart = lastWeek.startOf("week");
      setDateLabel(`Week of ${weekStart.format("D MMM YYYY")} · ${getWeekLabel(weekStart)}`);
    }
    if (key === "last-month") {
      const lastMonth = dayjs().subtract(1, "month");
      setPeriodAnchorDate(lastMonth);
      setDateLabel(lastMonth.format("MMMM YYYY"));
    }
    if (key === "all-time") setDateLabel("All time");
    if (key === "custom") {
      setStartDate(null);
      setEndDate(null);
      setActiveRangeField("start");
    }
  };

  const normalizeRange = (s, e) => {
    if (!s || !e) return [s, e];
    if (s.isAfter(e, "day")) return [e, s];
    return [s, e];
  };

  const today = dayjs().endOf("day");
  const isFutureDate = (value) => value && value.endOf("day").isAfter(today);

  const handleCalendarSelect = (value) => {
    if (isFutureDate(value)) return;
    setCalendarValue(value);
    if (selectedPreset !== "custom") return;
    if (activeRangeField === "start" || !startDate || (startDate && endDate)) {
      setStartDate(value);
      setEndDate(null);
      setActiveRangeField("end");
    } else {
      const [ns, ne] = normalizeRange(startDate, value);
      setStartDate(ns);
      setEndDate(ne);
      setActiveRangeField("start");
    }
  };

  const isBetween = (d, s, e) => {
    if (!s || !e) return false;
    return d.isAfter(s, "day") && d.isBefore(e, "day");
  };

  const dateRangeByPreset = useMemo(() => {
    const anchorDate = periodAnchorDate;
    const currentDate = dayjs();
    const clampEndDate = (dateValue) =>
      dateValue.isAfter(currentDate, "day") ? currentDate : dateValue;
    switch (selectedPreset) {
      case "today":
        return {
          start_date: anchorDate.format("YYYY-MM-DD"),
          end_date: clampEndDate(anchorDate).format("YYYY-MM-DD"),
        };
      case "this-week":
        return {
          start_date: anchorDate.startOf("week").format("YYYY-MM-DD"),
          end_date: clampEndDate(anchorDate.endOf("week")).format("YYYY-MM-DD"),
        };
      case "this-month":
        return {
          start_date: anchorDate.startOf("month").format("YYYY-MM-DD"),
          end_date: clampEndDate(anchorDate.endOf("month")).format("YYYY-MM-DD"),
        };
      case "this-quarter":
        {
          const month = anchorDate.month();
          const quarterStartMonth = Math.floor(month / 3) * 3;
          const qStart = anchorDate.month(quarterStartMonth).startOf("month");
          const qEnd = qStart.add(2, "month").endOf("month");
        return {
            start_date: qStart.format("YYYY-MM-DD"),
            end_date: clampEndDate(qEnd).format("YYYY-MM-DD"),
          };
        }
      case "this-year":
        return {
          start_date: anchorDate.startOf("year").format("YYYY-MM-DD"),
          end_date: clampEndDate(anchorDate.endOf("year")).format("YYYY-MM-DD"),
        };
      case "last-week":
        return {
          start_date: anchorDate.subtract(1, "week").startOf("week").format("YYYY-MM-DD"),
          end_date: anchorDate.subtract(1, "week").endOf("week").format("YYYY-MM-DD"),
        };
      case "last-month":
        return {
          start_date: anchorDate.subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
          end_date: anchorDate.subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
        };
      case "custom":
        return startDate && endDate
          ? { start_date: startDate.format("YYYY-MM-DD"), end_date: endDate.format("YYYY-MM-DD") }
          : {};
      default:
        return {};
    }
  }, [selectedPreset, startDate, endDate, periodAnchorDate]);

  useEffect(() => {
    if (selectedPreset === "all-time" || selectedPreset === "custom") return;
    const base = periodAnchorDate;
    if (selectedPreset === "today") setDateLabel(`Today · ${base.format("D MMM YYYY")}`);
    if (selectedPreset === "this-week" || selectedPreset === "last-week") {
      const weekStart = base.startOf("week");
      setDateLabel(`Week of ${weekStart.format("D MMM YYYY")} · ${getWeekLabel(weekStart)}`);
    }
    if (selectedPreset === "this-month" || selectedPreset === "last-month") {
      setDateLabel(base.format("MMMM YYYY"));
    }
    if (selectedPreset === "this-quarter") {
      const qStartMonth = Math.floor(base.month() / 3) * 3;
      const qStart = base.month(qStartMonth).startOf("month");
      const qEnd = qStart.add(2, "month").endOf("month");
      setDateLabel(`${qStart.format("MMM")} - ${qEnd.format("MMM YYYY")}`);
    }
    if (selectedPreset === "this-year") {
      setDateLabel(base.format("YYYY"));
    }
  }, [periodAnchorDate, selectedPreset]);

  const canShiftNext = useMemo(() => {
    const now = dayjs();
    if (selectedPreset === "all-time") return false;
    if (selectedPreset === "custom") {
      if (!endDate) return false;
      return endDate.isBefore(now, "day");
    }
    if (selectedPreset === "today") return periodAnchorDate.isBefore(now, "day");
    if (selectedPreset === "this-week" || selectedPreset === "last-week") {
      return periodAnchorDate.endOf("week").isBefore(now, "day");
    }
    if (selectedPreset === "this-month" || selectedPreset === "last-month") {
      return periodAnchorDate.endOf("month").isBefore(now, "day");
    }
    if (selectedPreset === "this-quarter") {
      const qStartMonth = Math.floor(periodAnchorDate.month() / 3) * 3;
      const qStart = periodAnchorDate.month(qStartMonth).startOf("month");
      const qEnd = qStart.add(2, "month").endOf("month");
      return qEnd.isBefore(now, "day");
    }
    if (selectedPreset === "this-year") {
      return periodAnchorDate.endOf("year").isBefore(now, "day");
    }
    return false;
  }, [selectedPreset, periodAnchorDate, endDate]);

  const handleShiftPeriod = (direction) => {
    if (direction === "next" && !canShiftNext) return;
    const step = direction === "next" ? 1 : -1;
    if (selectedPreset === "all-time") return;
    if (selectedPreset === "custom") {
      if (!startDate || !endDate) return;
      const diff = Math.max(endDate.diff(startDate, "day"), 1);
      setStartDate((prev) => prev?.add(step * diff, "day") || prev);
      setEndDate((prev) => prev?.add(step * diff, "day") || prev);
      return;
    }
    if (selectedPreset === "today") setPeriodAnchorDate((prev) => prev.add(step, "day"));
    else if (selectedPreset === "this-week" || selectedPreset === "last-week") {
      setPeriodAnchorDate((prev) => prev.add(step, "week"));
    } else if (selectedPreset === "this-month" || selectedPreset === "last-month") {
      setPeriodAnchorDate((prev) => prev.add(step, "month"));
    } else if (selectedPreset === "this-quarter") {
      setPeriodAnchorDate((prev) => prev.add(step * 3, "month"));
    } else if (selectedPreset === "this-year") {
      setPeriodAnchorDate((prev) => prev.add(step, "year"));
    }
    setCurrentPage(1);
  };

  const listQueryParams = useMemo(() => {
    const params = { page: currentPage };
    if (debouncedSearch) params.search = debouncedSearch;
    if (statusFilter !== "all") params.status = statusFilter;
    if (viewFilter !== "all") params.view = viewFilter;
    if (sortKey !== "newest_cart") params.sort_by = sortKey;
    if (dateRangeByPreset.start_date) params.start_date = dateRangeByPreset.start_date;
    if (dateRangeByPreset.end_date) params.end_date = dateRangeByPreset.end_date;
    return params;
  }, [currentPage, debouncedSearch, statusFilter, viewFilter, sortKey, dateRangeByPreset]);

  useEffect(() => {
    getAbandonedCartListApi(listQueryParams);
  }, [getAbandonedCartListApi, listQueryParams]);

  useEffect(() => {
    if (!(datePopoverOpen && selectedPreset === "custom")) return;
    const timer = setTimeout(() => {
      const startInput = customRangeWrapRef.current?.querySelector(
        ".ant-picker-input > input"
      );
      if (startInput) {
        startInput.focus();
        startInput.click();
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [datePopoverOpen, selectedPreset]);

  const dateRangeContent = (
    <div className={`ac-date-popover ${selectedPreset === "custom" ? "" : "ac-date-popover--single"}`}>
      <div className="ac-date-left">
        {presets.map((item, idx) =>
          item.divider ? (
            <div key={`d-${idx}`} className="ac-date-divider" />
          ) : (
            <button
              key={item.key}
              type="button"
              className={`ac-date-item ${selectedPreset === item.key ? "active" : ""}`}
              onClick={() => handlePresetClick(item.key)}
            >
              <div className="ac-date-item-title">
                {item.label}
                {item.default ? <span className="ac-default-badge">DEFAULT</span> : null}
                {item.custom ? <span className="ac-custom-arrow">›</span> : null}
              </div>
              {item.sub ? <div className="ac-date-item-sub">{item.sub}</div> : null}
            </button>
          )
        )}
      </div>
      {selectedPreset === "custom" ? (
        <div className="ac-date-right">
          <div className="ac-date-inputs" ref={customRangeWrapRef}>
            <RangePicker
              value={[startDate, endDate]}
              disabledDate={isFutureDate}
              onChange={(vals) => {
                const s = vals?.[0] || null;
                const e = vals?.[1] || null;
                const [ns, ne] = normalizeRange(s, e);
                setStartDate(ns);
                setEndDate(ne);
                if (s) setCalendarValue(s);
              }}
              onCalendarChange={(vals, _, info) => {
                if (info?.range) setActiveRangeField(info.range);
              }}
              placeholder={["START DATE", "END DATE"]}
              className="ac-range-picker"
              format="DD MMM YYYY"
            />
        </div>
        <Calendar
          fullscreen={false}
          value={calendarValue}
          disabledDate={isFutureDate}
          onPanelChange={(v) => {
            if (isFutureDate(v.endOf("month"))) return;
            setCalendarValue(v);
          }}
          onSelect={handleCalendarSelect}
          fullCellRender={(value) => {
            const isStart = startDate && value.isSame(startDate, "day");
            const isEnd = endDate && value.isSame(endDate, "day");
            const inRange = isBetween(value, startDate, endDate);
            const cls = [
              "ac-cal-cell",
              isStart ? "start" : "",
              isEnd ? "end" : "",
              inRange ? "in-range" : "",
            ]
              .filter(Boolean)
              .join(" ");
            return <div className={cls}>{value.date()}</div>;
          }}
          headerRender={({ value, onChange }) => (
            <div className="ac-cal-header">
              <Button
                type="text"
                icon={<CaretLeftOutlined />}
                onClick={() => onChange(value.clone().subtract(1, "month"))}
              />
              <div className="ac-cal-title">{value.format("MMMM YYYY")}</div>
              <Button
                type="text"
                icon={<CaretRightOutlined />}
                disabled={value.clone().add(1, "month").startOf("month").isAfter(dayjs(), "month")}
                onClick={() => onChange(value.clone().add(1, "month"))}
              />
            </div>
          )}
        />
        <div className="ac-date-actions">
          <Button onClick={() => { setDatePopoverOpen(false); }}>Cancel</Button>
          <Button
            type="primary"
            disabled={!startDate || !endDate}
            onClick={() => {
              setCurrentPage(1);
              if (startDate && endDate) {
                setDateLabel(
                  `${startDate.format("DD MMM YYYY")} - ${endDate.format("DD MMM YYYY")}`
                );
              }
              setDatePopoverOpen(false);
            }}
          >
            Apply
          </Button>
        </div>
      </div>
      ) : null}
    </div>
  );

  const summary = useMemo(() => {
    const analytics =
      abandonedCartListData?.data?.analytics ||
      abandonedCartListData?.analytics ||
      {};
    const abandonedOnly = apiRows.filter((row) => row.status === "abandoned");
    return {
      actionPending: Number(analytics?.action_pending?.count ?? abandonedOnly.length) || 0,
      unassigned:
        Number(
          analytics?.unassigned?.count ??
            abandonedOnly.filter((row) => !row.assigned_to).length
        ) || 0,
      hotLeads:
        Number(
          analytics?.hot_leads?.count ?? abandonedOnly.filter((row) => row.hot).length
        ) || 0,
      recovered:
        Number(
          analytics?.recovered?.count ??
            apiRows.filter((row) => row.status === "recovered").length
        ) || 0,
      atRiskValue:
        Number(
          analytics?.action_pending?.cart_amount ??
            abandonedOnly.reduce((sum, row) => sum + Number(row.cart_total || 0), 0)
        ) || 0,
      recoveredValue:
        Number(
          analytics?.recovered?.cart_amount ??
            apiRows
              .filter((row) => row.status === "recovered")
              .reduce((sum, row) => sum + Number(row.cart_total || 0), 0)
        ) || 0,
    };
  }, [apiRows, abandonedCartListData]);

  const filteredData = useMemo(() => apiRows, [apiRows]);

  const apiPagination =
    abandonedCartListData?.data?.pagination ||
    abandonedCartListData?.pagination ||
    {};
  const responsePage = Number(apiPagination?.page) || currentPage;
  const responseStartIndex = Number(apiPagination?.start_index) || 0;
  const responseEndIndex = Number(apiPagination?.end_index) || 0;
  const totalEntries = Number(apiPagination?.total_entries) || 0;
  const apiPageSize =
    responsePage > 1 && responseStartIndex > 1
      ? Math.round((responseStartIndex - 1) / (responsePage - 1))
      : apiPagination?.is_next_page && filteredData.length
      ? filteredData.length
      : Math.max(responseEndIndex - responseStartIndex + 1, filteredData.length, 1);

  const apiFilters =
    abandonedCartListData?.data?.filters || abandonedCartListData?.filters || {};

  const viewOptions = (
    Array.isArray(apiFilters?.view)
      ? apiFilters.view
      : ["all", "assigned_to_me", "unassigned", "hot_leads"]
  ).map((value) => ({
    value,
    label:
      value === "all"
        ? "View: All"
        : value === "assigned_to_me"
        ? "Assigned to me"
        : value === "unassigned"
        ? "Unassigned"
        : value === "hot_leads"
        ? "Hot leads"
        : value,
  }));

  const statusOptions = (
    Array.isArray(apiFilters?.status)
      ? apiFilters.status
      : ["all", "abandoned", "recovered", "lost"]
  ).map((value) => ({
    value,
    label:
      value === "all"
        ? getStatusOptionLabel("Status")
        : getStatusOptionLabel(
            value.charAt(0).toUpperCase() + value.slice(1).replaceAll("_", " ")
          ),
  }));

  const sortOptions = (
    Array.isArray(apiFilters?.sort_by)
      ? apiFilters.sort_by
      : ["newest_cart", "oldest_cart", "price_low_to_high", "price_high_to_low"]
  ).map((value) => ({
    value,
    label:
      value === "newest_cart"
        ? "Sort: Newest"
        : value === "oldest_cart"
        ? "Sort: Oldest"
        : value === "price_low_to_high"
        ? "Price Low → High"
        : value === "price_high_to_low"
        ? "Price High → Low"
        : value,
  }));

  const showListSkeleton = isListFetching && !apiRows.length;

  const handleAssignUpdate = async (row, assignee) => {
    const payload = assignee?.id ? { assigned_to: assignee.id } : { assigned_to: null };
    const response = await updateAbandonedCartStatusApi({
      id: row.id,
      body: payload,
    });
    if (response?.error) {
      showErrorToast(response?.error?.data?.message || "Failed to update assignee");
      return;
    }
    setAssignedById((prev) => ({ ...prev, [row.id]: assignee?.value || undefined }));
    showSuccessToast("Assignee updated");
    getAbandonedCartListApi(listQueryParams);
  };

  const handleDownloadChange = async () => {
    const downloadParams = { ...listQueryParams };
    delete downloadParams.page;
    const response = await getDownloadAbandonedCartReportApi(downloadParams);
    if (response?.error) {
      showErrorToast(response?.error?.data?.message || "Failed to download abandoned cart report");
      return;
    }

    const responseData = response?.data;
    const downloadUrl =
      responseData?.file_url ||
      responseData?.data?.url ||
      responseData?.data?.download_url ||
      responseData?.data?.file_url ||
      responseData?.url ||
      responseData?.download_url;
    if (!downloadUrl) {
      showErrorToast("Download URL not found");
      return;
    }

    const fileUrl = /^https?:\/\//i.test(downloadUrl) ? downloadUrl : `${baseUrl}${downloadUrl}`;
    saveAs(fileUrl);
    showSuccessToast(responseData?.message || "Abandoned cart report downloaded");
  };

  const columns = [
    {
      title: "Cart #",
      dataIndex: "id",
      width: 180,
      render: (id, row) => (
        <div
          className="cart-cell"
          title="Open cart details"
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/admin/customer/abandoned-carts/${row.id}`)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              navigate(`/admin/customer/abandoned-carts/${row.id}`);
            }
          }}
          style={{ cursor: "pointer" }}
        >
          <span className="hot-flame">{row.hot ? "🔥" : ""}</span>
          <div className="cart-num">#{id}</div>
        </div>
      ),
    },
    {
      title: "Customer",
      dataIndex: "customer_name",
      width: 260,
      render: (_, row) => (
        <div>
          <div className="cust-name">{row.customer_name}</div>
          <div className="cust-meta">{row.customer_mobile}</div>
        </div>
      ),
    },
    { title: "Location", dataIndex: "location", width: 180, render: (v) => <span className="location-cell">{v}</span> },
    { title: "Items", dataIndex: "items_summary", width: 220, render: (v) => <span className="items-primary">{v}</span> },
    {
      title: "Cart Value",
      dataIndex: "cart_total",
      align: "right",
      width: 180,
      render: (_, row) => (
        <div>
          <div className="value-amount">{formatMoney(row.cart_total)}</div>
          {row?.last_item_activity_at ? (
            <div className="value-time">{dateFormat(row.last_item_activity_at)}</div>
          ) : null}
        </div>
      ),
    },
    {
      title: "Assigned To",
      dataIndex: "assigned",
      width: 220,
      render: (_, row) => (
        <div className="assign-wrap" onClick={(e) => e.stopPropagation()}>
          <Popover
            trigger="click"
            placement="bottomLeft"
            open={assignOpenFor === row.id}
            onOpenChange={(open) => {
              setAssignOpenFor(open ? row.id : null);
              if (!open) setAssignSearch("");
            }}
            overlayClassName="assign-popover-overlay"
            content={
              <div className="assign-popover-card" onClick={(e) => e.stopPropagation()}>
                <AntInput
                  className="assign-search-input"
                  placeholder="Search teammates..."
                  value={assignSearch}
                  onChange={(e) => setAssignSearch(e.target.value)}
                />
                <div className="assign-options-list">
                  {assigneeOptions
                    .filter((opt) =>
                      !assignSearch.trim()
                        ? true
                        : `${opt.label} ${opt.searchText}`.toLowerCase().includes(assignSearch.toLowerCase())
                    )
                    .map((opt) => (
                      <button
                        type="button"
                        key={opt.value}
                        className="assign-option-row"
                        onClick={() => {
                          handleAssignUpdate(row, opt);
                          setAssignOpenFor(null);
                          setAssignSearch("");
                        }}
                      >
                        <span className={`assign-opt-avatar ${opt.chip}`}>{opt.initials}</span>
                        <span className="assign-opt-name">
                          {opt.label}
                        </span>
                      </button>
                    ))}
                </div>
              </div>
            }
          >
            {assignedById[row.id] ? (
              (() => {
                const current = assigneeOptions.find((opt) => opt.value === assignedById[row.id]);
                return (
                  <span className={`assign-chip ${current?.chip || "chip-blue"}`}>
                    <span className="avatar">{current?.initials || "NA"}</span>
                    <span className="name">{current?.label || assignedById[row.id]}</span>
                    <button
                      type="button"
                      className="remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAssignUpdate(row, null);
                      }}
                    >
                      ×
                    </button>
                  </span>
                );
              })()
            ) : (
              <button type="button" className="assign-empty-btn-inline">
                + Assign
              </button>
            )}
          </Popover>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 140,
      render: (s) => <span className={`status status-${s}`}><span className="dot" />{statusLabelMap[s]}</span>,
    },
  ];

  return (
    <div className={`abandoned-page ${rootClassName}`.trim()}>
      {showHeader ? (
        <div className="page-header">
          <div className="page-header-row">
            <div>
              <h1>Abandoned Carts</h1>
              <p>Capture, work, and close every abandoned cart in one place.</p>
            </div>
            <Space.Compact className="date-period-pill ant-period-pill">
              <Button
                className="dpp-arrow"
                icon={<LeftOutlined />}
                onClick={() => handleShiftPeriod("prev")}
                disabled={selectedPreset === "all-time"}
              />
              <Popover
                content={dateRangeContent}
                trigger="click"
                open={datePopoverOpen}
                onOpenChange={setDatePopoverOpen}
                placement="bottomRight"
                overlayClassName="ac-date-overlay"
              >
                <Button className="dpp-main" icon={<CalendarOutlined />}>
                  {dateLabel}
                </Button>
              </Popover>
              <Button
                className="dpp-arrow"
                icon={<RightOutlined />}
                onClick={() => handleShiftPeriod("next")}
                disabled={!canShiftNext}
              />
            </Space.Compact>
          </div>
        </div>
      ) : null}
      {showInfoBanner ? (
        <div className="ac-info-banner">
          <InfoCircleOutlined className="ac-info-icon" />
          <span>
            Shows carts left inactive for 10+ minutes after items are added.
            Carts valued above ₹20,000 are marked as 🔥 Hot Leads.
          </span>
        </div>
      ) : null}

      {showSummary ? (
        <div className="summary-grid">
          {showListSkeleton ? (
            Array.from({ length: 4 }).map((_, idx) => (
              <div className="summary-card" key={`sum-sk-${idx}`}>
                <Skeleton paragraph={{ rows: 2, width: ["50%", "70%"] }} title={{ width: "60%" }} active />
              </div>
            ))
          ) : (
            <>
              <div className="summary-card"><div className="summary-card-label">Action pending</div><div className="value">{summary.actionPending}</div><div className="value-sub alert">{formatMoney(summary.atRiskValue)} at risk</div></div>
              <div className="summary-card"><div className="summary-card-label">Unassigned</div><div className="value">{summary.unassigned}</div></div>
              <div className="summary-card"><div className="summary-card-label">Hot leads (₹20k+)</div><div className="value">{summary.hotLeads}</div></div>
              <div className="summary-card"><div className="summary-card-label">Recovered (7d)</div><div className="value">{summary.recovered}</div><div className="value-sub success">{formatMoney(summary.recoveredValue)} recovered</div></div>
            </>
          )}
        </div>
      ) : null}

      <div className="table-card">
        {tableTitle ? (
          <div className="ac-dashboard-table-title">
            <span>{tableTitle}</span>
            <button
              type="button"
              className="ac-dashboard-view-all"
              onClick={() => navigate("/admin/customer/abandoned-carts")}
            >
              View All
            </button>
          </div>
        ) : null}
        {showToolbar ? (
          <div className="toolbar ant-toolbar">
            <Select
              value={viewFilter}
              onChange={(v) => {
                setCurrentPage(1);
                setViewFilter(v);
              }}
              className="toolbar-select"
              options={viewOptions}
            />
            <Input
              allowClear
              className="table-search ant-table-search"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => {
                setCurrentPage(1);
                setSearchText(e.target.value);
              }}
              placeholder="Search cart #, customer, location..."
            />
            <div className="toolbar-spacer" />
            <Select
              value={statusFilter}
              onChange={(v) => {
                setCurrentPage(1);
                setStatusFilter(v);
              }}
              className="toolbar-select"
              options={statusOptions}
            />
            <Select
              value={sortKey}
              onChange={(v) => {
                setCurrentPage(1);
                setSortKey(v);
              }}
              className="toolbar-select"
              options={sortOptions}
            />
            <CustomSelect
              placeholder={"Download"}
              width={"100px"}
              customStyle="toolbar-select"
              options={downloadOptions}
              handleChange={handleDownloadChange}
              value={"EXPORT"}
            />
          </div>
        ) : null}

        {showListSkeleton ? (
          <div className="ac-list-skeleton-table">
            {Array.from({ length: 6 }).map((_, idx) => (
              <div className="ac-list-skeleton-row" key={`row-sk-${idx}`}>
                <Skeleton active paragraph={{ rows: 1, width: ["95%"] }} title={false} />
              </div>
            ))}
          </div>
        ) : (
          <Table
            className="abandoned-ant-table"
            rowKey="id"
            columns={columns}
            dataSource={filteredData}
            pagination={{
              current: currentPage,
              pageSize: apiPageSize,
              showSizeChanger: false,
              total: totalEntries || filteredData.length,
              onChange: (page) => setCurrentPage(page),
              showTotal: (total) =>
                responseStartIndex && responseEndIndex
                  ? `Showing ${responseStartIndex}-${responseEndIndex} of ${total} carts`
                  : `Showing ${filteredData.length} of ${total} carts`,
            }}
            scroll={{ x: 1300 }}
            rowClassName={(record) => (record.hot ? "ant-hot-row" : "")}
          />
        )}
      </div>
    </div>
  );
};

export default AbandonedCartList;
